import React, {useState, useEffect, useCallback} from 'react';
import { Button, Typography, Box, Autocomplete, CircularProgress, Alert, TextField } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, getContests } from '../api/contests';
import DynamicFormFields, { FormField } from './DynamicFormFields';
import {debounce } from 'lodash';
import { normalizeNewResultForRanking } from '../utils/resultParsing';

// Telegram user comes from Telegram WebApp initDataUnsafe.
// In production we MUST NOT fall back to a fake user (it would allow anonymous submissions).
// For local/debug you can enable a controlled override via env flags.
const tg = (window as any)?.Telegram?.WebApp;

type TelegramUser = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  allows_write_to_pm?: boolean;
};

const DEBUG_TG_ENABLED = (process.env.REACT_APP_TG_DEBUG ?? '') === '1';
const DEBUG_TG_USER_ID = Number(process.env.REACT_APP_TG_DEBUG_USER_ID ?? '0');

const getTelegramUser = (): TelegramUser | null => {
  try {
    const unsafeUser = tg?.initDataUnsafe?.user as TelegramUser | undefined;
    if (unsafeUser?.id) return unsafeUser;
  } catch {
    // ignore
  }

  // Optional debug override: only for a specific developer account.
  // Usage (local): REACT_APP_TG_DEBUG=1; REACT_APP_TG_DEBUG_USER_ID=123456
  if (DEBUG_TG_ENABLED && DEBUG_TG_USER_ID > 0) {
    return {
      id: DEBUG_TG_USER_ID,
      first_name: 'Serge',
      last_name: 'Debug',
      username: 'debug',
      language_code: 'ru',
      is_premium: true,
      allows_write_to_pm: true,
    };
  }

  return null;
};

interface Contest {
  contest_id: number;
  name: string;
}

const evidenceUrlField: FormField = {
  field_name: 'evidence_url',
  display_label: 'Введите ссылку на видео',
  data_type: 'text',
  required: false,
};

const TelegramAddContestEntry: React.FC = () => {
  const telegramUser = getTelegramUser();
  const isAuthorized = !!telegramUser;
  const [contests, setContests] = useState<Contest[]>([]);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [participantData, setParticipantData] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [mainResultField, setMainResultField] = useState<string | null>(null);
  const [resultCalcRule, setResultCalcRule] = useState<any>(null);
  const [ranking, setRanking] = useState<number | null>(null);
  const [suggestedRank, setSuggestedRank] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [loadingContests, setLoadingContests] = useState(true);
  const [loadingForm, setLoadingForm] = useState(false);
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);

  const participantName = telegramUser
    ? (telegramUser.username || `${telegramUser.first_name} ${telegramUser.last_name || ''}`.trim())
    : '';
  const contestId = selectedContest?.contest_id;

  useEffect(() => {
    getContests()
      .then((data) => setContests(data))
      .catch((error) => console.error('Error fetching contests:', error))
      .finally(() => setLoadingContests(false));
  }, []);

  useEffect(() => {
    if (contestId) {
      setLoadingForm(true);
      axios
        .get(`${API_BASE_URL}/contests/${contestId}`)
        .then((response) => {
          const metadata = response.data?.form_metadata;
          const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
          const fields = parsedMetadata.fields?.filter((f: any) => !f.non_entry_field) || [];
          if (fields?.length > 0) fields.push(evidenceUrlField);

          setFormFields(fields);
          setMainResultField(fields.find((f: FormField) => f.main_result_field)?.field_name || null);
          setResultCalcRule(parsedMetadata.result_calc || null);
          setFormData({});
          setRanking(null);
          setSuggestedRank(null);
        })
        .catch((error) => console.error('Error fetching form metadata:', error))
        .finally(() => setLoadingForm(false));
    }
  }, [contestId]);

  const handleInputChange = (name: string, value: any) => {
    if (name === 'participant_data') {
      setParticipantData(value);
    } else if (name === 'evidence_url') {
      setEvidenceUrl(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const calculateResult = (rule: any, data: { [key: string]: any }): number | null => {
    if (!rule || !rule.fields) return null;
    const { op, fields } = rule;
    const values: number[] = fields.map((field: any) => parseFloat(data[field]) || 0);

    switch (op) {
      case 'sum': return values.reduce((acc, val) => acc + val, 0);
      case 'diff': return values.reduce((acc, val) => acc - val);
      case 'mult': return values.reduce((acc, val) => acc * val, 1);
      case 'div': return values.reduce((acc, val) => (val !== 0 ? acc / val : acc));
      default: return null;
    }
  };

  const debounceCheckRanking = useCallback(
    debounce((contestId, grouping, requiredResult) => {
      if (!telegramUser) {
        setRanking(null);
        setSuggestedRank(null);
        return;
      }
      const normalized = normalizeNewResultForRanking({ fields: formFields, requiredResult });
      if (normalized === null) {
        setRanking(null);
        setSuggestedRank(null);
        return;
      }
  
      axios
        .get(`${API_BASE_URL}/telegram/new-ranking`, {
          params: { contest_id: contestId, grouping, new_result: normalized, tg_id: telegramUser.id },
        })
        .then((response) => {
          setRanking(response.data.ranking);
          setSuggestedRank(response.data.rank?.rank_type ?? null);
        })
        .catch(() => {
          setRanking(null);
          setSuggestedRank(null);
        });
    }, 300),
    [formFields, telegramUser]
  );
  

  useEffect(() => {
    const requiredResult = resultCalcRule
      ? calculateResult(resultCalcRule, formData)
      : formData[mainResultField || ''];
  
    if (contestId && formData.grouping && requiredResult !== undefined) {
      const normalized = normalizeNewResultForRanking({ fields: formFields, requiredResult });
      if (normalized !== null) {
        debounceCheckRanking(contestId, formData.grouping, normalized);
      }
    }
  }, [contestId, formData, resultCalcRule, mainResultField, debounceCheckRanking, formFields]);
  

  const allRequiredFieldsFilled = formFields.every((field) => !field.required || formData[field.field_name]);
  const shouldDisableSubmit = !allRequiredFieldsFilled  || !evidenceUrl;

  const handleSubmit = () => {
    if (!telegramUser) {
      setSubmissionError('Не авторизован: откройте форму из Telegram.');
      setHasSubmitted(true);
      return;
    }
    setIsSubmitting(true);
    axios
      .post(`${API_BASE_URL}/telegram/submit-result`, {
        participant_name: participantName,
        first_name: telegramUser.first_name,
        last_name: telegramUser.last_name,
        username: telegramUser.username,
        tg_id: telegramUser.id,
        is_premium: telegramUser.is_premium,
        contest_name: selectedContest?.name,
        contest_id: contestId,
        entry_data: {...formData, participant_data: participantData, evidence_url: evidenceUrl},
      })
      .then(() => setSubmissionSuccess(true))
      .catch(() => setSubmissionError('Произошла ошибка. Попробуйте ещё раз или сообщите администратору.'))
      .finally(() => {
        setHasSubmitted(true);
        setIsSubmitting(false);
      });
  };

  return (
    <Box p={3}>
      {!isAuthorized ? (
        <>
          <Typography variant="h5" gutterBottom>Не авторизован</Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            Откройте эту страницу из Telegram (WebApp), чтобы отправить результат.
          </Alert>
        </>
      ) : (
        <Typography variant="h5" gutterBottom>Приветствую, {participantName}!</Typography>
      )}
      {submissionSuccess && <Alert severity="success" sx={{ mb: 2 }}>✅ Ваш результат отправлен!</Alert>}
      {submissionError && <Alert severity="error" sx={{ mb: 2 }}>⚠️ {submissionError}</Alert>}

      {loadingContests ? (
        <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
      ) : (
        <Autocomplete
          options={contests}
          getOptionLabel={(option) => option.name}
          onChange={(event, value) => setSelectedContest(value)}
          renderInput={(params) => <TextField {...params} label="Выберите зарубу" variant="outlined" />}
          fullWidth
          style={{ marginBottom: '16px', marginTop: 20 }}
        />
      )}

      {selectedContest && (
        <>
          {loadingForm ? (
            <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
          ) : (
            <DynamicFormFields formFields={formFields} formData={{ ...formData, participant_data: participantData, evidence_url: evidenceUrl }} onInputChange={handleInputChange} contestId={contestId?.toString()} />
          )}

          <TextField color="primary"
            label="Предварительный рейтинг"
            value={ranking ?? '—'}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            label="Разряд (если выполнен)"
            value={suggestedRank ?? '—'}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
            margin="normal"
            variant="outlined"
          />

          {ranking === 1 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Ваш результат претендует на 1-е место, желаю удачи!
            </Alert>
          )
          }
          {submissionSuccess && <Alert severity="success" sx={{ mb: 2 }}>✅ Ваш результат отправлен!</Alert>}
          {submissionError && <Alert severity="error" sx={{ mb: 2 }}>⚠️ {submissionError}</Alert>}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isAuthorized || shouldDisableSubmit || isSubmitting || hasSubmitted }
          >
            Послать результат
          </Button>
        </>
      )}
    </Box>
  );
};

export default TelegramAddContestEntry;
